import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  View,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppButton from "@toothfairy/shared-ui/AppButton";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";

/**
 * AgentWorkflowBuilder - React Flow v12 based visual workflow builder
 *
 * Features:
 * - Drag and drop nodes on canvas
 * - Connect nodes with edges to create dependencies
 * - Custom node types for agent steps
 * - Inline editing panel
 * - Auto-layout
 * - Import/Export JSON
 *
 * Output: { plan: [...steps] }
 */

// Custom Node Component for Agent Steps
const AgentStepNode = ({ data, selected }) => {
  const { currentTheme } = AppTheme.useTheme();

  // Custom font families
  const fontNormal =
    "normalFont, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const fontBold =
    "boldFont, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return currentTheme?.theme?.green || "#34C759";
      case "failed":
        return currentTheme?.theme?.red || "#FF3B30";
      case "in_progress":
        return currentTheme?.theme?.orange || "#FF9500";
      default:
        return currentTheme?.theme?.primary || "#007AFF";
    }
  };

  return (
    <div
      style={{
        background: selected
          ? (currentTheme?.theme?.primary || "#007AFF") + "20"
          : currentTheme?.theme?.cardBackground ||
            currentTheme?.theme?.white ||
            "#ffffff",
        border: `2px solid ${
          selected
            ? currentTheme?.theme?.primary
            : currentTheme?.theme?.border_color || "#e0e0e0"
        }`,
        borderRadius: 12,
        padding: 12,
        minWidth: 220,
        maxWidth: 280,
        boxShadow: selected
          ? `0 4px 12px ${currentTheme?.theme?.primary || "#007AFF"}40`
          : "0 2px 8px rgba(0,0,0,0.15)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: fontNormal,
      }}
      onClick={() => data.onSelect(data.step)}
    >
      {/* Status Bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: getStatusColor(data.step.status),
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
        }}
      />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: currentTheme?.theme?.primary,
          width: 10,
          height: 10,
        }}
      />

      {/* Content */}
      <div style={{ marginTop: 8 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor:
                (currentTheme?.theme?.primary || "#007AFF") + "20",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: "bold",
                color: currentTheme?.theme?.primary,
                fontFamily: fontBold,
              }}
            >
              {data.step.step_id}
            </span>
          </div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: currentTheme?.theme?.font_color || "#000",
              fontFamily: fontBold,
            }}
          >
            Step {data.step.step_id}
          </span>
        </div>

        {/* Task Description */}
        <p
          style={{
            fontSize: 13,
            color: currentTheme?.theme?.font_color || "#333",
            margin: "0 0 8px 0",
            lineHeight: 1.4,
            maxHeight: 60,
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: fontNormal,
          }}
        >
          {data.step.task_description || "No description"}
        </p>

        {/* Agent Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 8,
          }}
        >
          <AppIcon
            icon={AppAssets?.icons?.planner}
            size={12}
            color={currentTheme?.theme?.darkGrey}
          />
          <span
            style={{
              fontSize: 12,
              color: currentTheme?.theme?.darkGrey || "#666",
              fontFamily: fontNormal,
            }}
          >
            {data.getAgentName(data.step.agent_id)}
          </span>
        </div>

        {/* Badges */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {data.step.authorisation_required && (
            <span
              style={{
                padding: "2px 8px",
                backgroundColor:
                  (currentTheme?.theme?.orange || "#FF9500") + "20",
                color: currentTheme?.theme?.orange,
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 500,
                fontFamily: fontBold,
              }}
            >
              Auth
            </span>
          )}
          {data.step.computer_required && (
            <span
              style={{
                padding: "2px 8px",
                backgroundColor:
                  (currentTheme?.theme?.blue || "#007AFF") + "20",
                color: currentTheme?.theme?.blue,
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 500,
                fontFamily: fontBold,
              }}
            >
              PC
            </span>
          )}
          {data.step.allow_parallel && (
            <span
              style={{
                padding: "2px 8px",
                backgroundColor:
                  (currentTheme?.theme?.green || "#34C759") + "20",
                color: currentTheme?.theme?.green,
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 500,
                fontFamily: fontBold,
              }}
            >
              Parallel
            </span>
          )}
          {data.step.parallel_group && (
            <span
              style={{
                padding: "2px 8px",
                backgroundColor:
                  (currentTheme?.theme?.purple || "#AF52DE") + "20",
                color: currentTheme?.theme?.purple,
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 500,
                fontFamily: fontBold,
              }}
            >
              {data.step.parallel_group}
            </span>
          )}
        </div>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: currentTheme?.theme?.primary,
          width: 10,
          height: 10,
        }}
      />
    </div>
  );
};

// Parallel Group Node - Container for parallel steps
const ParallelGroupNode = ({ data }) => {
  const { currentTheme } = AppTheme.useTheme();

  return (
    <div
      style={{
        background: (currentTheme?.theme?.green || "#34C759") + "10",
        border: `2px dashed ${currentTheme?.theme?.green || "#34C759"}`,
        borderRadius: 16,
        padding: 20,
        minWidth: 700,
        minHeight: 180,
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -12,
          left: 20,
          background: currentTheme?.theme?.white || "#fff",
          padding: "2px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          color: currentTheme?.theme?.green || "#34C759",
          fontFamily: "boldFont, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        Parallel Group: {data.groupName}
      </div>
      {/* Input Handle for the group */}
      <Handle
        type="target"
        position={Position.Left}
        id="group-input"
        style={{
          background: currentTheme?.theme?.green || "#34C759",
          width: 12,
          height: 12,
          top: "50%",
        }}
      />
    </div>
  );
};

// Node Types Registry
const nodeTypes = {
  agentStep: AgentStepNode,
  parallelGroup: ParallelGroupNode,
};

const AgentWorkflowBuilder = forwardRef(
  (
    {
      value,
      onChange,
      darkMode = false,
      label = "Workflow Builder",
      agents = [],
    },
    ref,
  ) => {
    const { currentTheme } = AppTheme.useTheme();
    const [steps, setSteps] = useState([]);
    const [selectedStep, setSelectedStep] = useState(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [validationError, setValidationError] = useState(null);

    // Expose current steps to parent via ref
    useImperativeHandle(ref, () => ({
      getCurrentSteps: () => steps,
      syncToFormik: () => {
        if (!validationError && onChange) {
          onChange({ plan: steps });
        }
      },
    }));

    // Ref to prevent recursive updates
    const lastEmittedValueRef = useRef(null);
    const isProcessingIncomingValueRef = useRef(false);

    // Validation function
    const validatePlanSteps = (planSteps) => {
      const errors = [];

      if (!Array.isArray(planSteps)) {
        return ["Plan must be an array of steps"];
      }

      planSteps.forEach((step, index) => {
        const stepNum = index + 1;

        if (!step.step_id) {
          errors.push(`Step ${stepNum}: Missing step_id`);
        }
        if (!step.agent_id) {
          errors.push(`Step ${stepNum}: Missing agent_id`);
        }
        if (!step.task_description) {
          errors.push(`Step ${stepNum}: Missing task_description`);
        }
        if (!Array.isArray(step.depends_on)) {
          errors.push(`Step ${stepNum}: depends_on must be an array`);
        }
        if (
          step.authorisation_required !== undefined &&
          typeof step.authorisation_required !== "boolean"
        ) {
          errors.push(
            `Step ${stepNum}: authorisation_required must be boolean`,
          );
        }
        if (
          step.computer_required !== undefined &&
          typeof step.computer_required !== "boolean"
        ) {
          errors.push(`Step ${stepNum}: computer_required must be boolean`);
        }
        if (
          step.allow_parallel !== undefined &&
          typeof step.allow_parallel !== "boolean"
        ) {
          errors.push(`Step ${stepNum}: allow_parallel must be boolean`);
        }
      });

      return errors;
    };

    // Normalize dependencies for parallel groups
    const normalizeParallelDependencies = (stepsList) => {
      // Identify parallel groups
      const parallelGroups = {};
      stepsList.forEach((step) => {
        if (step.allow_parallel && step.parallel_group) {
          if (!parallelGroups[step.parallel_group]) {
            parallelGroups[step.parallel_group] = [];
          }
          parallelGroups[step.parallel_group].push(step);
        }
      });
      
      // For each parallel group with 2+ members, normalize dependencies
      return stepsList.map((step) => {
        if (step.allow_parallel && step.parallel_group && parallelGroups[step.parallel_group]?.length >= 2) {
          const groupMembers = parallelGroups[step.parallel_group];
          const groupMemberIds = new Set(groupMembers.map(s => s.step_id));
          
          // Collect all external dependencies from all group members
          const allExternalDeps = new Set();
          groupMembers.forEach(member => {
            member.depends_on.forEach(depId => {
              if (!groupMemberIds.has(depId)) {
                allExternalDeps.add(depId);
              }
            });
          });
          
          // Set the step's dependencies to all external dependencies
          const normalizedDeps = [...allExternalDeps].sort((a, b) => a - b);
          
          if (JSON.stringify(normalizedDeps) !== JSON.stringify(step.depends_on.sort((a, b) => a - b))) {
            return { ...step, depends_on: normalizedDeps };
          }
        }
        return step;
      });
    };

    // Parse incoming value with validation
    useEffect(() => {
      // Skip if this value matches what we just emitted (prevents loop)
      const valueStr = JSON.stringify(value);
      if (valueStr === lastEmittedValueRef.current) {
        // Reset the ref after skipping so future updates work
        // This allows the next external change to be processed
        lastEmittedValueRef.current = null;
        return;
      }

      isProcessingIncomingValueRef.current = true;
      setValidationError(null);

      if (!value) {
        setSteps([]);
        isProcessingIncomingValueRef.current = false;
        return;
      }

      if (typeof value !== "object") {
        setValidationError(
          "Invalid JSON: must be an object with a 'plan' property",
        );
        isProcessingIncomingValueRef.current = false;
        return;
      }

      let planSteps = [];
      if (Array.isArray(value)) {
        planSteps = value;
      } else if (value.plan && Array.isArray(value.plan)) {
        planSteps = value.plan;
      } else if (value.plan === undefined) {
        setValidationError("Missing 'plan' property in JSON");
        isProcessingIncomingValueRef.current = false;
        return;
      } else {
        setValidationError("Invalid 'plan' property: must be an array");
        isProcessingIncomingValueRef.current = false;
        return;
      }

      const errors = validatePlanSteps(planSteps);
      if (errors.length > 0) {
        setValidationError(errors.join("\n"));
        // Still set the steps so user can see and fix invalid data
        // Form validation will prevent saving
      }

      // Normalize dependencies for parallel groups
      const normalizedSteps = normalizeParallelDependencies(planSteps);
      setSteps(normalizedSteps);
      isProcessingIncomingValueRef.current = false;
    }, [value]);

    // Convert steps to React Flow nodes - with parallel group containers
    useEffect(() => {
      setNodes((existingNodes) => {
        const existingNodesMap = new Map(
          existingNodes.map((node) => [node.id, node.position]),
        );

        // Identify parallel groups
        const parallelGroups = {};
        const stepParallelGroup = {};
        steps.forEach((step) => {
          if (step.allow_parallel && step.parallel_group) {
            if (!parallelGroups[step.parallel_group]) {
              parallelGroups[step.parallel_group] = [];
            }
            parallelGroups[step.parallel_group].push(step);
            stepParallelGroup[step.step_id] = step.parallel_group;
          }
        });

        // Calculate Y position based on dependencies
        const stepBaseY = {};
        const visited = new Set();
        
        const calculateY = (stepId, depth = 0) => {
          if (visited.has(stepId)) return stepBaseY[stepId] || 0;
          visited.add(stepId);
          
          const step = steps.find(s => s.step_id === stepId);
          if (!step) return depth;
          
          let maxParentY = -1;
          step.depends_on.forEach(depId => {
            const parentY = calculateY(depId, depth);
            maxParentY = Math.max(maxParentY, parentY);
          });
          
          stepBaseY[stepId] = maxParentY >= 0 ? maxParentY + 1 : depth;
          return stepBaseY[stepId];
        };
        
        steps.forEach(step => calculateY(step.step_id));

        const flowNodes = [];
        
        // Create group nodes first
        Object.entries(parallelGroups).forEach(([groupName, groupSteps]) => {
          if (groupSteps.length >= 2) {
            const minGroupY = Math.min(...groupSteps.map(s => stepBaseY[s.step_id] || 0));
            const groupY = 100 + minGroupY * 250;
            
            const groupNodeId = `parallel-group-${groupName}`;
            const existingGroupPos = existingNodesMap.get(groupNodeId);
            
            // Calculate proper width: each step is ~260px wide + 60px gap between them + 80px padding on sides
            const stepWidth = 260;
            const stepGap = 60;
            const containerPadding = 80;
            const totalWidth = containerPadding * 2 + (groupSteps.length * stepWidth) + ((groupSteps.length - 1) * stepGap);
            
            flowNodes.push({
              id: groupNodeId,
              type: "parallelGroup",
              position: existingGroupPos || { x: 100, y: groupY },
              data: { groupName },
              style: { 
                width: totalWidth,
                height: 280,
              },
            });
          }
        });
        
        // Create step nodes (as children of groups if in parallel)
        steps.forEach((step) => {
          const nodeId = `step-${step.step_id}`;
          const existingPosition = existingNodesMap.get(nodeId);
          const parallelGroup = stepParallelGroup[step.step_id];
          const isInParallelGroup = parallelGroup && 
            parallelGroups[parallelGroup] && 
            parallelGroups[parallelGroup].length >= 2;
          
          let x = 100;
          let y = 100 + (stepBaseY[step.step_id] || 0) * 250;
          let parentId = undefined;
          let extent = undefined;
          
          if (isInParallelGroup) {
            parentId = `parallel-group-${parallelGroup}`;
            extent = "parent";
            
            const groupSteps = parallelGroups[parallelGroup];
            const indexInGroup = groupSteps.findIndex(s => s.step_id === step.step_id);
            
            // Position with proper spacing
            const stepWidth = 260;
            const stepGap = 60;
            const containerPadding = 80;
            x = containerPadding + indexInGroup * (stepWidth + stepGap);
            y = 80;
          }

          flowNodes.push({
            id: nodeId,
            type: "agentStep",
            position: existingPosition || { x, y },
            parentId,
            extent,
            data: {
              step,
              onSelect: (s) => setSelectedStep(s),
              getAgentName,
            },
          });
        });

        return flowNodes;
      });
    }, [steps]);

    // Convert depends_on to React Flow edges - connecting to parallel groups
    useEffect(() => {
      const flowEdges = [];
      
      // Identify parallel groups
      const parallelGroups = {};
      const stepParallelGroup = {};
      steps.forEach((step) => {
        if (step.allow_parallel && step.parallel_group) {
          if (!parallelGroups[step.parallel_group]) {
            parallelGroups[step.parallel_group] = [];
          }
          parallelGroups[step.parallel_group].push(step);
          stepParallelGroup[step.step_id] = step.parallel_group;
        }
      });
      
      // Track which groups already have connections from which predecessors
      const groupConnections = new Set();
      
      steps.forEach((step) => {
        const parallelGroup = stepParallelGroup[step.step_id];
        const isInParallelGroup = parallelGroup && 
          parallelGroups[parallelGroup] && 
          parallelGroups[parallelGroup].length >= 2;
        
        step.depends_on.forEach((depId) => {
          const depStep = steps.find(s => s.step_id === depId);
          const depIsInParallelGroup = depStep?.allow_parallel && 
            depStep?.parallel_group && 
            parallelGroups[depStep.parallel_group]?.length >= 2;
          
          // If current step is in a parallel group with 2+ members
          if (parallelGroup && parallelGroups[parallelGroup]?.length >= 2) {
            const groupNodeId = `parallel-group-${parallelGroup}`;
            const connectionKey = `edge-${depId}-to-group-${parallelGroup}`;
            
            // Only create one edge from predecessor to the group
            if (!groupConnections.has(connectionKey)) {
              groupConnections.add(connectionKey);
              
              // If the dependency is in a different parallel group or not in any group
              if (depStep?.parallel_group !== parallelGroup) {
                const sourceId = depIsInParallelGroup
                  ? `parallel-group-${depStep.parallel_group}`
                  : `step-${depId}`;
                
                flowEdges.push({
                  id: connectionKey,
                  source: sourceId,
                  target: groupNodeId,
                  targetHandle: "group-input",
                  animated: true,
                  style: {
                    stroke: currentTheme?.theme?.green || "#34C759",
                    strokeWidth: 3,
                    strokeDasharray: "8,4",
                  },
                  label: "parallel",
                  labelStyle: {
                    fill: currentTheme?.theme?.green || "#34C759",
                    fontWeight: 700,
                    fontSize: 11,
                  },
                  labelBgStyle: {
                    fill: currentTheme?.theme?.white || "#fff",
                    fillOpacity: 0.95,
                  },
                  labelBgPadding: [6, 3],
                  labelBgBorderRadius: 6,
                });
              }
            }
          } else {
            // Regular step - create normal edge
            flowEdges.push({
              id: `edge-${depId}-${step.step_id}`,
              source: `step-${depId}`,
              target: `step-${step.step_id}`,
              animated: true,
              style: {
                stroke: currentTheme?.theme?.primary || "#007AFF",
                strokeWidth: 2,
              },
            });
          }
        });
      });
      setEdges(flowEdges);
    }, [steps, currentTheme]);

    // Handle edge connections
    const onConnect = useCallback(
      (connection) => {
        const sourceId = parseInt(connection.source.replace("step-", ""));
        const targetId = parseInt(connection.target.replace("step-", ""));

        // Update target step's depends_on
        setSteps((prevSteps) =>
          prevSteps.map((step) => {
            if (step.step_id === targetId) {
              return {
                ...step,
                depends_on: [...new Set([...step.depends_on, sourceId])],
              };
            }
            return step;
          }),
        );

        setEdges((eds) =>
          addEdge(
            {
              ...connection,
              animated: true,
              style: {
                stroke: currentTheme?.theme?.primary || "#007AFF",
                strokeWidth: 2,
              },
            },
            eds,
          ),
        );
      },
      [setEdges, currentTheme],
    );

    // Handle edge deletion
    const onEdgesDelete = useCallback((deletedEdges) => {
      deletedEdges.forEach((edge) => {
        const sourceId = parseInt(edge.source.replace("step-", ""));
        const targetId = parseInt(edge.target.replace("step-", ""));

        setSteps((prevSteps) =>
          prevSteps.map((step) => {
            if (step.step_id === targetId) {
              return {
                ...step,
                depends_on: step.depends_on.filter((id) => id !== sourceId),
              };
            }
            return step;
          }),
        );
      });
    }, []);

    // Generate output
    useEffect(() => {
      // Don't generate output if there's a validation error or we're processing incoming value
      if (validationError || isProcessingIncomingValueRef.current) return;

      const output = { plan: steps };
      const outputStr = JSON.stringify(output);

      // Only call onChange if we haven't already emitted this exact value
      if (outputStr !== lastEmittedValueRef.current) {
        lastEmittedValueRef.current = outputStr;
        onChange?.(output);
      }
    }, [steps, onChange, validationError]);

    // Add new step
    const addStep = () => {
      const newStepId =
        steps.length > 0 ? Math.max(...steps.map((s) => s.step_id)) + 1 : 1;
      const newStep = {
        step_id: newStepId,
        agent_id: "",
        task_description: "",
        depends_on: [],
        authorisation_required: false,
        computer_required: false,
        allow_parallel: false,
        parallel_group: "",
        status: "pending",
      };
      setSteps([...steps, newStep]);
      setSelectedStep(newStep);
    };

    // Update step
    const updateStep = (stepId, updates) => {
      setSteps((prevSteps) => {
        let updatedSteps = prevSteps.map((step) =>
          step.step_id === stepId ? { ...step, ...updates } : step,
        );
        
        // Normalize dependencies when parallel settings change
        if (updates.allow_parallel !== undefined || updates.parallel_group !== undefined) {
          updatedSteps = normalizeParallelDependencies(updatedSteps);
        }
        
        return updatedSteps;
      });
      
      if (selectedStep?.step_id === stepId) {
        // Re-find the updated step from the normalized state
        setSteps((currentSteps) => {
          const updated = currentSteps.find(s => s.step_id === stepId);
          if (updated) {
            setSelectedStep(updated);
          }
          return currentSteps;
        });
      }
    };
    
    // Delete step
    const deleteStep = (stepId) => {
      setSteps(
        steps
          .filter((s) => s.step_id !== stepId)
          .map((s) => ({
            ...s,
            depends_on: s.depends_on.filter((id) => id !== stepId),
          })),
      );
      if (selectedStep?.step_id === stepId) {
        setSelectedStep(null);
      }
    };

    // Clear all
    const clearAll = () => {
      setSteps([]);
      setSelectedStep(null);
      setNodes([]);
      setEdges([]);
      onChange?.({ plan: [] });
    };

    const getAgentName = (agentId) => {
      const agent = agents.find((a) => a.id === agentId);
      return agent?.name || agentId?.slice(0, 8) + "..." || "No agent";
    };

    return (
      <View style={styles.container}>
        {/* Header */}
        <View
          style={[
            styles.header,
            {
              backgroundColor: darkMode
                ? currentTheme?.theme?.cardBackground
                : currentTheme?.theme?.white,
              borderBottomColor: currentTheme?.theme?.border_color,
            },
          ]}
        >
          <View style={styles.headerLeft}>
            <AppIcon
              icon={AppAssets?.icons?.plan}
              size={20}
              color={currentTheme?.theme?.primary}
            />
            <AppText
              preventTranslation
              style={[
                styles.headerTitle,
                { color: currentTheme?.theme?.font_color },
              ]}
            >
              {label}
            </AppText>
            <View
              style={[
                styles.stepCountBadge,
                { backgroundColor: currentTheme?.theme?.primary + "20" },
              ]}
            >
              <AppText
                preventTranslation
                style={[
                  styles.stepCountText,
                  { color: currentTheme?.theme?.white },
                ]}
              >
                {steps.length} steps
              </AppText>
            </View>
          </View>

          <View style={styles.headerButtons}>
            <AppText
              preventTranslation
              isClickable
              isHoverable={false}
              onPress={clearAll}
              style={[
                styles.headerButtonText,
                { color: currentTheme?.theme?.red },
              ]}
              wrapperStyle={{
                marginTop: 7,
              }}
            >
              Clear
            </AppText>
            <TouchableOpacity
              onPress={addStep}
              style={[
                styles.headerButton,
                styles.addButton,
                { backgroundColor: currentTheme?.theme?.primary },
              ]}
            >
              <AppText
                preventTranslation
                style={[styles.headerButtonText, styles.addButtonText]}
              >
                + Add Step
              </AppText>
            </TouchableOpacity>
          </View>
        </View>

        {/* Validation Error Display */}
        {validationError ? (
          <View style={styles.canvas}>
            <AppPlaceholderView
              accessibilityLabel="workflow-validation-error"
              wrapperStyle={{
                width: 300,
                height: 200,
                marginVertical: "auto",
                marginHorizontal: "auto",
              }}
              size={30}
              icon={AppAssets?.icons?.warning || AppAssets?.icons?.error}
              mainCaption="Invalid Workflow JSON"
              subCaption={`${validationError}\n\nPlease correct the mistake in the JSON editor.`}
            />
          </View>
        ) : (
          <>
            {/* React Flow Canvas */}
            <View style={styles.canvas}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgesDelete={onEdgesDelete}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{
                  minZoom: 0.1,
                  maxZoom: 1.5,
                  padding: 0.25, // Increased padding for better node spacing
                }}
                attributionPosition="bottom-left"
                colorMode={darkMode ? "dark" : "light"}
              >
                <Background
                  color={currentTheme?.theme?.border_color || "#e0e0e0"}
                  gap={16}
                  size={1}
                />
                <Controls />
                <Panel position="top-right">
                  {/* <div
              style={{
                background: currentTheme?.theme?.white || "#fff",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12,
                color: currentTheme?.theme?.darkGrey || "#666",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Drag to connect • Click node to edit
            </div> */}
                </Panel>
              </ReactFlow>
            </View>

            {/* Edit Panel */}
            {selectedStep && (
              <StepEditPanel
                step={selectedStep}
                onUpdate={(updates) =>
                  updateStep(selectedStep.step_id, updates)
                }
                onDelete={() => deleteStep(selectedStep.step_id)}
                onClose={() => setSelectedStep(null)}
                allSteps={steps}
                agents={agents}
                getAgentName={getAgentName}
                currentTheme={currentTheme}
                darkMode={darkMode}
              />
            )}
          </>
        )}
      </View>
    );
  },
);

/**
 * Step Edit Panel Component
 */
const StepEditPanel = ({
  step,
  onUpdate,
  onDelete,
  onClose,
  allSteps,
  agents,
  getAgentName,
  currentTheme,
  darkMode,
}) => {
  return (
    <View
      style={[
        styles.editPanel,
        {
          backgroundColor: darkMode
            ? currentTheme?.theme?.cardBackground
            : currentTheme?.theme?.white,
          borderLeftColor: currentTheme?.theme?.border_color,
          height: "99%",
          marginTop: 7.5,
        },
      ]}
    >
      {/* Panel Header */}
      <View
        style={[
          styles.editPanelHeader,
          { borderBottomColor: currentTheme?.theme?.border_color },
        ]}
      >
        <AppText
          preventTranslation
          style={[
            styles.editPanelTitle,
            { color: currentTheme?.theme?.font_color },
          ]}
        >
          Edit Step {step.step_id}
        </AppText>
        <TouchableOpacity onPress={onClose}>
          <AppIcon
            icon={AppAssets?.icons?.close}
            size={20}
            color={currentTheme?.theme?.font_color}
          />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.editPanelContent}>
        {/* Agent Selection */}
        <View style={styles.editField}>
          <AppText
            preventTranslation
            style={[styles.editLabel, { color: currentTheme?.theme?.darkGrey }]}
          >
            Agent *
          </AppText>
          {agents.length > 0 ? (
            <AppDropDown
              showTextInputLabel={false}
              label=""
              inputLabelStyle={{
                marginLeft: 0,
              }}
              dropDownStyle={{
                minWidth: 100,
                borderColor: currentTheme?.theme?.border_color,
              }}
              isElevated={false}
              dropdownIconColor="transparent"
              items={[
                { id: "", name: "Select an agent" },
                { id: "new_agent", name: "Dynamic agent" },
                ...agents
                  .filter((agent) =>
                    ["retriever", "coder", "chatter"].includes(agent.mode),
                  )
                  .map((agent) => ({
                    id: agent.id,
                    name: agent.name || agent.id?.slice(0, 8) + "...",
                  })),
              ]}
              propertyLabel="name"
              propertyValue="id"
              selectedValue={step.agent_id}
              onValueChange={(itemValue) => {
                onUpdate({ agent_id: itemValue });
              }}
            />
          ) : (
            <TextInput
              value={step.agent_id}
              onChangeText={(text) => onUpdate({ agent_id: text })}
              placeholder="Enter agent UUID"
              style={[
                styles.textInput,
                {
                  borderColor: currentTheme?.theme?.border_color,
                  backgroundColor: darkMode
                    ? currentTheme?.theme?.inputBackgroundColor
                    : currentTheme?.theme?.white,
                  color: currentTheme?.theme?.font_color,
                },
              ]}
              placeholderTextColor={currentTheme?.theme?.grey}
            />
          )}
        </View>

        {/* Task Description */}
        <View style={styles.editField}>
          <AppText
            preventTranslation
            style={[styles.editLabel, { color: currentTheme?.theme?.darkGrey }]}
          >
            Task Description *
          </AppText>
          <AppTextInput
            value={step.task_description}
            onChangeText={(text) => onUpdate({ task_description: text })}
            placeholder="Describe what this step should accomplish..."
            multiline={true}
            numberOfLines={4}
            textContainer={{
              minHeight: 100,
              padding: 10,
              paddingRight: 5,
              paddingLeft: 10,
              marginLeft: 0,

              margin: 0,
              marginRight: 0,
            }}
            wrapperInputStyle={{
              borderRadius: 10,
              borderColor: currentTheme?.theme?.border_color,
              backgroundColor: darkMode
                ? currentTheme?.theme?.inputBackgroundColor
                : currentTheme?.theme?.white,
            }}
          />
        </View>

        {/* Dependencies */}
        <View style={styles.editField}>
          <AppText
            preventTranslation
            style={[styles.editLabel, { color: currentTheme?.theme?.darkGrey }]}
          >
            Depends On
          </AppText>
          <View style={styles.dependenciesGrid}>
            {allSteps.filter((s) => String(s.step_id) !== String(step.step_id))
              .length === 0 ? (
              <AppText
                preventTranslation
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.grey,
                  fontStyle: "italic",
                }}
              >
                No other steps available for deps.
              </AppText>
            ) : (
              allSteps
                .filter((s) => String(s.step_id) !== String(step.step_id))
                .map((s) => {
                  const isSelected = step.depends_on.some(
                    (id) => String(id) === String(s.step_id),
                  );
                  return (
                    <TouchableOpacity
                      key={s.step_id}
                      onPress={() => {
                        const newDeps = isSelected
                          ? step.depends_on.filter(
                              (id) => String(id) !== String(s.step_id),
                            )
                          : [...step.depends_on, s.step_id];
                        onUpdate({ depends_on: newDeps });
                      }}
                      style={[
                        styles.dependencyChip,
                        {
                          backgroundColor: isSelected
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.white,
                          borderColor: isSelected
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.border_color,
                        },
                      ]}
                    >
                      <AppText
                        preventTranslation
                        style={[
                          styles.dependencyChipText,
                          {
                            color: isSelected
                              ? currentTheme?.theme?.white
                              : currentTheme?.theme?.primary,
                          },
                        ]}
                      >
                        Step {s.step_id}
                      </AppText>
                    </TouchableOpacity>
                  );
                })
            )}
          </View>
        </View>

        {/* Parallel Group */}
        <View style={styles.editField}>
          <AppText
            preventTranslation
            style={[styles.editLabel, { color: currentTheme?.theme?.darkGrey }]}
          >
            Parallel Group (optional)
          </AppText>
          <TextInput
            value={step.parallel_group}
            onChangeText={(text) => onUpdate({ parallel_group: text })}
            placeholder="Group name"
            style={[
              styles.textInput,
              {
                borderColor: currentTheme?.theme?.border_color,
                backgroundColor: darkMode
                  ? currentTheme?.theme?.inputBackgroundColor
                  : currentTheme?.theme?.white,
                color: currentTheme?.theme?.font_color,
              },
            ]}
            placeholderTextColor={currentTheme?.theme?.grey}
          />
        </View>

        {/* Toggles */}
        <View style={styles.togglesContainer}>
          <ToggleRow
            icon={AppAssets?.icons?.lock}
            iconColor={currentTheme?.theme?.primary}
            label="Authorisation Required"
            value={step.authorisation_required}
            onChange={(val) => onUpdate({ authorisation_required: val })}
            currentTheme={currentTheme}
          />
          <ToggleRow
            icon={AppAssets?.icons?.desktop}
            iconColor={currentTheme?.theme?.primary}
            label="Computer Required"
            value={step.computer_required}
            onChange={(val) => onUpdate({ computer_required: val })}
            currentTheme={currentTheme}
          />
          <ToggleRow
            icon={AppAssets?.icons?.fork}
            iconColor={currentTheme?.theme?.primary}
            label="Allow Parallel"
            value={step.allow_parallel}
            onChange={(val) => onUpdate({ allow_parallel: val })}
            currentTheme={currentTheme}
          />
        </View>

        {/* Delete Button */}
        <AppText
          preventTranslation
          isClickable
          isHoverable={false}
          onPress={onDelete}
          wrapperStyle={{
            justifyContent: "center",
          }}
          style={[styles.deleteButtonText, { color: currentTheme?.theme?.red }]}
        >
          Delete Step
        </AppText>
      </ScrollView>
    </View>
  );
};

/**
 * Toggle Row Component
 */
const ToggleRow = ({
  icon,
  iconColor,
  label,
  value,
  onChange,
  currentTheme,
}) => {
  return (
    <View style={styles.toggleRow}>
      <View style={styles.toggleLeft}>
        <AppIcon icon={icon} size={16} color={iconColor} />
        <AppText
          preventTranslation
          style={[
            styles.toggleLabel,
            { color: currentTheme?.theme?.font_color },
          ]}
        >
          {label}
        </AppText>
      </View>
      <AppSwitch
        showTextInputLabel={false}
        isTicked={value}
        onChange={onChange}
        wrapperStyle={{
          paddingVertical: 2,
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 12,
    overflow: "hidden",
    height: 600,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  stepCountBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  stepCountText: {
    fontSize: 12,
    fontWeight: "600",
  },
  headerButtons: {
    flexDirection: "row",
    gap: 8,
  },
  headerButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  addButton: {
    borderRadius: 6,
  },
  headerButtonText: {
    fontSize: 13,
    fontWeight: "600",
  },
  addButtonText: {
    color: "#FFFFFF",
  },
  canvas: {
    flex: 1,
  },
  editPanel: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 320,
    borderLeftWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  editPanelHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  editPanelTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  editPanelContent: {
    flex: 1,
    padding: 16,
  },
  editField: {
    marginBottom: 16,
  },
  editLabel: {
    fontSize: 13,
    marginBottom: 6,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    fontSize: 14,
  },
  textArea: {
    height: 80,
    textAlignVertical: "top",
  },
  agentGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  agentChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  agentChipText: {
    fontSize: 13,
    fontWeight: "500",
  },
  dependenciesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  dependencyChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  dependencyChipText: {
    fontSize: 13,
    fontWeight: "500",
  },
  togglesContainer: {
    gap: 4,
    marginBottom: 8,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  toggleLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toggleLabel: {
    fontSize: 14,
  },
  deleteButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: "600",
  },
});

export default AgentWorkflowBuilder;
